/**
 * 顧客詳細「基本情報」タブ。
 *
 * 左カラム: 編集可能な基本情報フォーム + 背景記録 (salesMemo)
 * 右カラム: 行動履歴タイムライン (audience_events ベース)
 *
 * 住所モデル (v3.2.0):
 *   - 請求先住所 / 納品先住所はどちらも配列管理 (BillingAddress[] / DeliveryAddress[])
 *   - 既定フラグ (isDefault) は配列内で常に最大 1 件
 *   - 旧データ (postalCode/prefecture/address/buildingName 直下) は getBillingAddresses で互換読み取り
 *   - 保存時は billingAddresses[] + 旧フィールドへ同期書込 (mailRender / 既存PDFの後方互換)
 */
import type {
  BillingAddress,
  ContactKind,
  DeliveryAddress,
  EnglishAddressFieldKey,
  MailContact,
} from '@goenhub/shared';
import {
  COUNTRY_ADDRESS_CONFIGS,
  SA_RETENTION_DEFAULT_MONTHS,
  SA_RETENTION_DEFAULT_REMINDER_DAYS,
  SA_RETENTION_REASON_LABEL,
  buildLegacyAddressFields,
  computeRetentionUntil,
  getBillingAddresses,
  getCountryAddressConfig,
  resolveRetentionState,
} from '@goenhub/shared';
import {
  Building2,
  CalendarClock,
  ClipboardList,
  Mail,
  MapPin,
  Plus,
  Save,
  Star,
  Trash2,
  UserCircle2,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import {
  useContactTimeline,
  type ContactTimelineEvent,
} from '@/hooks/useContactTimeline';
import {
  useExtendSaRetention,
  useUpdateMailContact,
} from '@/hooks/useMailContacts';
import { useSaAgents } from '@/hooks/useSaAgents';
import { useSalesSettingsWithDefaults } from '@/hooks/useSettings';
import { formatDate, toDate } from '@/lib/firestoreTime';

interface Props {
  contactId: string;
  contact: MailContact;
}

const KIND_OPTIONS: Array<{ value: ContactKind; label: string }> = [
  { value: 'btob', label: 'BtoB (法人)' },
  { value: 'btoc', label: 'BtoC (個人)' },
  { value: 'unknown', label: '未設定' },
];

const EVENT_LABEL: Record<string, string> = {
  mail_sent: 'メール送信',
  mail_open: 'メール開封',
  line_news_received: 'LINE 受信',
  form_submitted: 'フォーム送信',
  landing_view: 'ランディング閲覧',
};

const TARGET_PREFIX: Record<string, string> = {
  mail_schedule: 'メール',
  line_news: 'ニュース',
  form: 'フォーム',
  landing: 'LP',
};

function formatTimestamp(ts: ContactTimelineEvent['timestamp']): string {
  if (!ts) return '';
  try {
    return ts.toDate().toLocaleString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** 配列要素の編集用フォーム型 (BillingAddress / DeliveryAddress 共通)。 */
type AddressForm = {
  id: string;
  label: string;
  recipientName: string;
  postalCode: string;
  prefecture: string;
  address: string;
  buildingName: string;
  phone: string;
  isDefault: boolean;
};

function newAddressForm(isFirstEntry: boolean): AddressForm {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `addr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    label: '',
    recipientName: '',
    postalCode: '',
    prefecture: '',
    address: '',
    buildingName: '',
    phone: '',
    isDefault: isFirstEntry,
  };
}

function toAddressForm(a: BillingAddress | DeliveryAddress): AddressForm {
  return {
    id: a.id,
    label: a.label ?? '',
    recipientName: a.recipientName ?? '',
    postalCode: a.postalCode ?? '',
    prefecture: a.prefecture ?? '',
    address: a.address ?? '',
    buildingName: a.buildingName ?? '',
    phone: a.phone ?? '',
    isDefault: a.isDefault ?? false,
  };
}

/** 保存直前のサニタイズ。空エントリ除外＋isDefault を最大 1 件に正規化。 */
function sanitizeAddresses<T extends BillingAddress | DeliveryAddress>(
  list: AddressForm[],
): T[] {
  const trimmed = list
    .map((a) => ({
      id: a.id,
      label: a.label.trim() || null,
      recipientName: a.recipientName.trim() || null,
      postalCode: a.postalCode.trim() || null,
      prefecture: a.prefecture.trim() || null,
      address: a.address.trim() || null,
      buildingName: a.buildingName.trim() || null,
      phone: a.phone.trim() || null,
      isDefault: a.isDefault,
    }))
    .filter(
      (a) =>
        a.label ||
        a.recipientName ||
        a.postalCode ||
        a.prefecture ||
        a.address ||
        a.buildingName ||
        a.phone,
    );
  if (trimmed.length === 0) return [];
  const defaultIdx = trimmed.findIndex((a) => a.isDefault);
  const winnerIdx = defaultIdx >= 0 ? defaultIdx : 0;
  return trimmed.map((a, i) => ({ ...a, isDefault: i === winnerIdx })) as T[];
}

export function ContactBasicTab({ contactId, contact }: Props) {
  const update = useUpdateMailContact();
  const timeline = useContactTimeline({
    contactId,
    lineUid: contact.lineUid ?? null,
    limit: 50,
  });

  // ローカル編集ステート (contact が変わったら同期)
  const [form, setForm] = useState(() => initForm(contact));
  useEffect(() => {
    setForm(initForm(contact));
  }, [contact]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(initForm(contact));

  const handleSave = async () => {
    const finalBilling = sanitizeAddresses<BillingAddress>(form.billingAddresses);
    const finalDelivery = sanitizeAddresses<DeliveryAddress>(form.deliveryAddresses);
    const legacy = buildLegacyAddressFields(finalBilling);

    // 英表記住所 (国 or いずれかのフィールドがあれば保存、全空なら null)
    const enAddrObj = {
      country: form.enCountry.trim() || null,
      postalCode: form.enAddr.postalCode.trim() || null,
      stateRegion: form.enAddr.stateRegion.trim() || null,
      city: form.enAddr.city.trim() || null,
      line1: form.enAddr.line1.trim() || null,
      line2: form.enAddr.line2.trim() || null,
    };
    const hasEnAddr = !!(
      enAddrObj.country ||
      enAddrObj.postalCode ||
      enAddrObj.stateRegion ||
      enAddrObj.city ||
      enAddrObj.line1 ||
      enAddrObj.line2
    );

    try {
      await update.mutateAsync({
        id: contactId,
        data: {
          name: form.name,
          lastName: form.lastName.trim() || null,
          firstName: form.firstName.trim() || null,
          lastNameKana: form.lastNameKana.trim() || null,
          firstNameKana: form.firstNameKana.trim() || null,
          phone: form.phone.trim() || null,
          kind: form.kind,
          companyName: form.companyName || null,
          department: form.department || null,
          jobTitle: form.jobTitle || null,
          birthdate: form.birthdate || null,
          salesMemo: form.salesMemo || null,
          isTestContact: form.isTestContact,
          billingAddresses: finalBilling,
          deliveryAddresses: finalDelivery,
          // 旧フィールドへの同期書込 (既定の請求先 = 単一フィールド)
          postalCode: legacy.postalCode,
          prefecture: legacy.prefecture,
          address: legacy.address,
          buildingName: legacy.buildingName,
          // 英表記登録情報 (ドイツ報告対象)
          nameEn: form.nameEn.trim() || null,
          phoneIntl: form.phoneIntl.trim() || null,
          englishAddress: hasEnAddr ? enAddrObj : null,
        },
      });
      toast.success('基本情報を更新しました');
    } catch (err) {
      toast.error(`更新に失敗しました: ${(err as Error).message}`);
    }
  };

  // ── 住所配列の汎用操作 (請求先/納品先で共有) ──
  const setAddressList = (
    kind: 'billingAddresses' | 'deliveryAddresses',
    next: AddressForm[],
  ) => setForm({ ...form, [kind]: next });

  const addAddress = (kind: 'billingAddresses' | 'deliveryAddresses') => {
    const cur = form[kind];
    setAddressList(kind, [...cur, newAddressForm(cur.length === 0)]);
  };

  const updateAddress = (
    kind: 'billingAddresses' | 'deliveryAddresses',
    id: string,
    patch: Partial<AddressForm>,
  ) => {
    setAddressList(
      kind,
      form[kind].map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );
  };

  const removeAddress = (
    kind: 'billingAddresses' | 'deliveryAddresses',
    id: string,
  ) => {
    const next = form[kind].filter((a) => a.id !== id);
    if (next.length > 0 && !next.some((a) => a.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    setAddressList(kind, next);
  };

  const setDefaultAddress = (
    kind: 'billingAddresses' | 'deliveryAddresses',
    id: string,
  ) => {
    setAddressList(
      kind,
      form[kind].map((a) => ({ ...a, isDefault: a.id === id })),
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
      {/* 左カラム: 基本情報フォーム */}
      <div className="space-y-5">
        <Section title="基本情報">
          <Field label="氏名 (表示名) *">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="姓">
              <Input
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                placeholder="例: 山田"
              />
            </Field>
            <Field label="名">
              <Input
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                placeholder="例: 太郎"
              />
            </Field>
            <Field label="せい (フリガナ)">
              <Input
                value={form.lastNameKana}
                onChange={(e) =>
                  setForm({ ...form, lastNameKana: e.target.value })
                }
                placeholder="例: ヤマダ"
              />
            </Field>
            <Field label="めい (フリガナ)">
              <Input
                value={form.firstNameKana}
                onChange={(e) =>
                  setForm({ ...form, firstNameKana: e.target.value })
                }
                placeholder="例: タロウ"
              />
            </Field>
            <Field label="電話番号">
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="例: 090-0000-0000"
              />
            </Field>
            <Field label="生年月日 (西暦)">
              <Input
                type="date"
                value={form.birthdate}
                onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
              />
            </Field>
          </div>
          {/* 会社名は BtoB なら下の「法人情報」セクションで編集。それ以外はここに出す
              (フォーム自動反映は kind に依らず companyName へ書くため、常にどこかで見えるように) */}
          {form.kind !== 'btob' && (
            <Field label="会社名">
              <Input
                value={form.companyName}
                onChange={(e) =>
                  setForm({ ...form, companyName: e.target.value })
                }
                placeholder="法人のお客様のみ"
              />
            </Field>
          )}
          <Field label="顧客タイプ">
            <div className="flex gap-2">
              {KIND_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, kind: opt.value })}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors flex items-center gap-1.5 ${
                    form.kind === opt.value
                      ? 'bg-[var(--accent-subtle)] border-[var(--accent)] text-[var(--accent)]'
                      : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {opt.value === 'btob' ? (
                    <Building2 className="size-3.5" />
                  ) : opt.value === 'btoc' ? (
                    <UserCircle2 className="size-3.5" />
                  ) : null}
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="メールアドレス (既定)">
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <Mail className="size-3.5 text-[var(--text-muted)]" />
              {contact.defaultEmail || '—'}
              <span className="text-xs text-[var(--text-muted)]">
                (一覧画面・メール配信から自動設定)
              </span>
            </div>
          </Field>
        </Section>

        <OwnerSaSection contactId={contactId} contact={contact} />

        {form.kind === 'btob' ? (
          <Section title="法人情報 (BtoB)">
            <Field label="会社名">
              <Input
                value={form.companyName}
                onChange={(e) =>
                  setForm({ ...form, companyName: e.target.value })
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="部署">
                <Input
                  value={form.department}
                  onChange={(e) =>
                    setForm({ ...form, department: e.target.value })
                  }
                />
              </Field>
              <Field label="役職">
                <Input
                  value={form.jobTitle}
                  onChange={(e) =>
                    setForm({ ...form, jobTitle: e.target.value })
                  }
                />
              </Field>
            </div>
          </Section>
        ) : form.kind === 'btoc' ? null : (
          <Section
            title="顧客タイプ未設定"
            description="上の「BtoB / BtoC」を選択すると関連項目を入力できます。"
          >
            <p className="text-xs text-[var(--text-muted)] italic">—</p>
          </Section>
        )}

        <Section
          title="請求先住所"
          description="請求書・見積書の宛先で使用。複数登録できます。「★ 既定」が常に 1 件選ばれます。"
        >
          <AddressListEditor
            entries={form.billingAddresses}
            emptyLabel="請求先住所が登録されていません。"
            placeholderPrefix="請求先"
            onUpdate={(id, patch) => updateAddress('billingAddresses', id, patch)}
            onRemove={(id) => removeAddress('billingAddresses', id)}
            onSetDefault={(id) => setDefaultAddress('billingAddresses', id)}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => addAddress('billingAddresses')}
          >
            <Plus className="size-3.5" />
            請求先を追加
          </Button>
        </Section>

        <Section
          title="納品先住所"
          description="商品の発送・納品で使用。請求先と異なる場合や、複数の現場/営業所への納品を想定して複数登録できます。「★ 既定」が常に 1 件選ばれます。"
        >
          <AddressListEditor
            entries={form.deliveryAddresses}
            emptyLabel="納品先住所が登録されていません。"
            placeholderPrefix="納品先"
            onUpdate={(id, patch) => updateAddress('deliveryAddresses', id, patch)}
            onRemove={(id) => removeAddress('deliveryAddresses', id)}
            onSetDefault={(id) => setDefaultAddress('deliveryAddresses', id)}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => addAddress('deliveryAddresses')}
          >
            <Plus className="size-3.5" />
            納品先を追加
          </Button>
        </Section>

        <Section
          title="英表記登録情報 (海外メーカー向け)"
          description="海外メーカー (ドイツ) への購入者報告に使用する英語表記の氏名・電話・住所。ドイツへの報告状況は「ドイツ報告」タブで確認できます。"
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="英語名 (Name)">
              <Input
                value={form.nameEn}
                onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                placeholder="例: Taro Yamada"
              />
            </Field>
            <Field label="国番号付き電話 (Phone)">
              <Input
                value={form.phoneIntl}
                onChange={(e) => setForm({ ...form, phoneIntl: e.target.value })}
                placeholder="例: +81 90-1234-5678"
              />
            </Field>
          </div>
          <Field label="国 (Country)">
            <select
              value={form.enCountry}
              onChange={(e) => setForm({ ...form, enCountry: e.target.value })}
              className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-primary)]"
            >
              <option value="">未選択</option>
              {COUNTRY_ADDRESS_CONFIGS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.nameJa} ({c.name})
                </option>
              ))}
            </select>
          </Field>
          {form.enCountry && (
            <div className="grid grid-cols-2 gap-3">
              {getCountryAddressConfig(form.enCountry).fields.map((f) => (
                <Field key={f.key} label={`${f.labelJa} (${f.label})`}>
                  <Input
                    value={form.enAddr[f.key]}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        enAddr: { ...form.enAddr, [f.key]: e.target.value },
                      })
                    }
                    placeholder={f.placeholder}
                  />
                </Field>
              ))}
            </div>
          )}
        </Section>

        <Section
          title="背景記録"
          description="この顧客固有のコンテキストを残す自由記述欄。決裁者・予算・歴史的経緯など、商談を跨いで持っておきたい情報。"
        >
          <textarea
            value={form.salesMemo}
            onChange={(e) => setForm({ ...form, salesMemo: e.target.value })}
            rows={6}
            className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] resize-y"
            placeholder="例: 決裁者は田中部長。予算は年度末締めで¥500万。過去にX社製品を導入していた。"
          />
        </Section>

        <Section
          title="テスト顧客"
          description="動作確認用フラグ。ON の顧客はフォーム送信のたびに必ず新しい商談が作られ、既存商談への自動マージも保留トレイへの計上も行われません。本番のお客様には付けないでください。"
        >
          <Switch
            label="この顧客をテスト用として扱う"
            checked={form.isTestContact}
            onChange={(e) =>
              setForm({ ...form, isTestContact: e.target.checked })
            }
          />
        </Section>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border)]">
          {isDirty && (
            <span className="text-xs text-[var(--accent-amber)]">
              未保存の変更があります
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={!isDirty || update.isPending}
            variant="primary"
          >
            <Save className="size-3.5" />
            {update.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>

      {/* 右カラム: 行動履歴タイムライン */}
      <div>
        <Section
          title="行動履歴"
          description="この顧客がメール開封・フォーム送信・LP 閲覧などで会社に接触した履歴。"
        >
          {timeline.isLoading ? (
            <Spinner />
          ) : !timeline.data || timeline.data.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="行動履歴がありません"
              description="メール送信・フォーム送信などのイベントが蓄積されると表示されます。"
            />
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {timeline.data.map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-start gap-3 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)]"
                >
                  <div className="text-[10px] text-[var(--text-muted)] tabular-nums whitespace-nowrap pt-0.5">
                    {formatTimestamp(ev.timestamp)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-[var(--text-primary)]">
                      {EVENT_LABEL[ev.type] ?? ev.type}
                    </div>
                    {ev.targetLabel && (
                      <div className="text-[11px] text-[var(--text-muted)] truncate">
                        {TARGET_PREFIX[ev.targetType] ?? ''}: {ev.targetLabel}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 担当SA (mail_contacts.ownerSaUid)
// ─────────────────────────────────────────────

const ASSIGNED_VIA_LABEL: Record<string, string> = {
  form: 'フォーム自動 (SAリンク経由)',
  manual: '手動設定',
};

/**
 * 顧客の担当SA表示 / 変更。
 *
 * ownerSaUid は SAポータル >「自分の顧客」の唯一の判定キーであり、
 * firestore.rules で SA 本人の read 許可条件にもなっている
 * (= ここを変えると、その SA から顧客が見える / 見えなくなる)。
 *
 * 表示名は ownerSaName スナップショットを基本とし、admins を読める owner のみ
 * 実データで解決し直す (admins は owner 以外 read 不可のため)。
 */
function OwnerSaSection({
  contactId,
  contact,
}: {
  contactId: string;
  contact: MailContact;
}) {
  const { user } = useAuth();
  const update = useUpdateMailContact();
  const { merged: salesSettings } = useSalesSettingsWithDefaults();
  const isOwner = user?.role === 'owner';
  // admins の read は owner 限定。editor 等ではクエリ自体を無効化してエラーを避ける。
  const saAgents = useSaAgents({ enabled: isOwner });

  const retentionMonths =
    salesSettings.saCustomerRetentionMonths ?? SA_RETENTION_DEFAULT_MONTHS;
  const ownerSaUid = contact.ownerSaUid ?? '';

  const saOptions = useMemo(
    () =>
      (saAgents.data ?? []).filter(
        (a) => (a.status ?? 'active') === 'active' || a.uid === ownerSaUid,
      ),
    [saAgents.data, ownerSaUid],
  );

  const liveName = useMemo(() => {
    const hit = (saAgents.data ?? []).find((a) => a.uid === ownerSaUid);
    return hit ? hit.fullName || hit.displayName || hit.email || null : null;
  }, [saAgents.data, ownerSaUid]);

  // owner は実データ優先 (SA 改名に追従)、それ以外はスナップショットを表示。
  const shownName = liveName || contact.ownerSaName || null;

  const handleChange = async (nextUid: string) => {
    const next = nextUid || null;
    if ((contact.ownerSaUid ?? null) === next) return;
    const hit = (saAgents.data ?? []).find((a) => a.uid === next);
    // 担当を付け替えたら保有期限も張り直す (紐付け時点 + 設定ヶ月)。
    const now = new Date();
    const until = computeRetentionUntil(now, retentionMonths);
    try {
      await update.mutateAsync({
        id: contactId,
        data: next
          ? {
              ownerSaUid: next,
              ownerSaName:
                (hit && (hit.fullName || hit.displayName || hit.email)) || next,
              ownerSaAssignedVia: 'manual',
              ownerSaAssignedAt: now.toISOString(),
              saRetentionUntil: until ? until.toISOString() : null,
              saRetentionMonths: until ? retentionMonths : null,
              saRetentionStartedAt: now.toISOString(),
              saRetentionReason: 'assigned',
              saRetentionReminderSentFor: null,
            }
          : {
              ownerSaUid: null,
              ownerSaName: null,
              ownerSaAssignedVia: null,
              ownerSaAssignedAt: null,
              saRetentionUntil: null,
              saRetentionMonths: null,
              saRetentionStartedAt: null,
              saRetentionReason: null,
              saRetentionReminderSentFor: null,
            },
      });
      toast.success(next ? '担当SAを更新しました' : '担当SAを解除しました');
    } catch (err) {
      toast.error(`更新に失敗しました: ${(err as Error).message}`);
    }
  };

  return (
    <Section
      title="担当SA (外部営業)"
      description="この顧客を担当する SA。SAリンク付き営業フォームからの申請で自動的に設定され (先勝ち)、SAポータルの「自分の顧客」に表示されます。"
    >
      <div className="flex items-center gap-2">
        <Users className="size-3.5 text-[var(--text-muted)] flex-shrink-0" />
        {ownerSaUid ? (
          <span className="text-sm text-[var(--text-primary)]">
            {shownName ?? '(名前解決できませんでした)'}
          </span>
        ) : (
          <span className="text-sm text-[var(--text-muted)]">
            未設定 (社内管理の顧客)
          </span>
        )}
      </div>

      {ownerSaUid && (
        <div className="text-[11px] text-[var(--text-muted)] space-y-0.5">
          <div>
            紐付け経路:{' '}
            {ASSIGNED_VIA_LABEL[contact.ownerSaAssignedVia ?? ''] ?? '不明 (旧データ)'}
            {contact.ownerSaAssignedAt
              ? ` / ${formatDate(contact.ownerSaAssignedAt)}`
              : ''}
          </div>
          <div className="font-mono opacity-70">uid: {ownerSaUid}</div>
        </div>
      )}

      {ownerSaUid && (
        <RetentionRow
          contactId={contactId}
          contact={contact}
          isOwner={isOwner}
        />
      )}

      {!ownerSaUid && contact.saReleasedFromSaUid && (
        <div className="text-[11px] text-[var(--text-muted)] rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1.5">
          保有期限切れにより{' '}
          <strong>{contact.saReleasedFromSaName || contact.saReleasedFromSaUid}</strong>{' '}
          の担当から外れました
          {contact.saReleasedAt ? ` (${formatDate(contact.saReleasedAt)})` : ''}。
          再度担当に戻す場合は下のドロップダウンから設定してください。
        </div>
      )}

      {isOwner ? (
        <Field label="担当SAを変更">
          <select
            value={ownerSaUid}
            onChange={(e) => void handleChange(e.target.value)}
            disabled={update.isPending || saAgents.isLoading}
            className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-primary)]"
          >
            <option value="">未設定 (社内管理)</option>
            {saOptions.map((a) => (
              <option key={a.uid} value={a.uid}>
                {a.fullName || a.displayName || a.email}
                {a.email ? ` (${a.email})` : ''}
              </option>
            ))}
            {ownerSaUid && !saOptions.some((a) => a.uid === ownerSaUid) && (
              <option value={ownerSaUid}>
                {shownName ?? ownerSaUid} (現状値・SA一覧に無し)
              </option>
            )}
          </select>
          <p className="text-[10px] text-[var(--text-muted)] mt-1">
            変更すると即時保存されます。解除すると、その SA のポータルからこの顧客は見えなくなります。
          </p>
        </Field>
      ) : (
        <p className="text-[10px] text-[var(--text-muted)] italic">
          担当SAの変更は owner のみ可能です。
        </p>
      )}
    </Section>
  );
}

const RETENTION_REASON_LABEL: Record<string, string> = SA_RETENTION_REASON_LABEL;

const RETENTION_STATE_STYLE: Record<
  string,
  { label: string; cls: string }
> = {
  active: {
    label: '期限内',
    cls: 'bg-[var(--accent-green-subtle)] text-[var(--accent-green)]',
  },
  expiring: {
    label: '期限間近',
    cls: 'bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]',
  },
  expired: {
    label: '期限切れ',
    cls: 'bg-[var(--accent-red-subtle)] text-[var(--accent-red)]',
  },
  none: {
    label: '無期限',
    cls: 'bg-[var(--bg-hover)] text-[var(--text-secondary)]',
  },
};

/**
 * SA 顧客保有期限の表示と手動延長。
 *
 * 期限は「SA に紐付いた時点」および「その SA の商談で入金があった時点」を起算に
 * settings/sales.saCustomerRetentionMonths ヶ月。期限切れは日次バッチが解除する。
 */
function RetentionRow({
  contactId,
  contact,
  isOwner,
}: {
  contactId: string;
  contact: MailContact;
  isOwner: boolean;
}) {
  const extend = useExtendSaRetention();
  const { merged: salesSettings } = useSalesSettingsWithDefaults();
  const reminderDays =
    salesSettings.saRetentionReminderDays ?? SA_RETENTION_DEFAULT_REMINDER_DAYS;

  const until = toDate(contact.saRetentionUntil);
  const { state, daysLeft } = resolveRetentionState({
    hasOwnerSa: !!contact.ownerSaUid,
    until,
    now: new Date(),
    reminderDays,
  });
  const style = RETENTION_STATE_STYLE[state] ?? RETENTION_STATE_STYLE.none;

  const handleExtend = async (mode: 'from_now' | 'from_until') => {
    try {
      const r = await extend.mutateAsync({ contactId, mode });
      toast.success(
        `保有期限を ${new Date(r.retentionUntil).toLocaleDateString('ja-JP')} まで延長しました`,
      );
    } catch (err) {
      toast.error(`延長に失敗しました: ${(err as Error).message}`);
    }
  };

  return (
    <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] p-2.5 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarClock className="size-3.5 text-[var(--text-muted)]" />
        <span className="text-xs text-[var(--text-secondary)]">顧客期限</span>
        <span className="text-sm font-semibold text-[var(--text-primary)]">
          {until ? until.toLocaleDateString('ja-JP') : '無期限'}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${style.cls}`}>
          {style.label}
          {until && state !== 'expired' ? ` (あと${daysLeft}日)` : ''}
        </span>
      </div>

      <div className="text-[11px] text-[var(--text-muted)]">
        {until ? (
          <>
            {contact.saRetentionMonths ? `${contact.saRetentionMonths}ヶ月` : ''}
            {contact.saRetentionReason
              ? ` / 起算: ${RETENTION_REASON_LABEL[contact.saRetentionReason] ?? contact.saRetentionReason}`
              : ''}
            {contact.saRetentionStartedAt
              ? ` (${formatDate(contact.saRetentionStartedAt)})`
              : ''}
            。この SA の商談で入金があると入金日を起算にリセットされます。
          </>
        ) : (
          <>
            保有期間が 0ヶ月 (無期限) 設定のため、期限切れによる解除は行われません。
          </>
        )}
      </div>

      {isOwner && (
        <div className="flex items-center gap-2 pt-0.5">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={extend.isPending}
            onClick={() => void handleExtend('from_now')}
          >
            今から延長
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={extend.isPending || !until || state === 'expired'}
            onClick={() => void handleExtend('from_until')}
            title={
              state === 'expired'
                ? '期限切れのため「今から延長」を使ってください'
                : '現在の期限から上乗せします'
            }
          >
            期限から上乗せ
          </Button>
          <span className="text-[10px] text-[var(--text-muted)]">
            設定値 {salesSettings.saCustomerRetentionMonths ?? SA_RETENTION_DEFAULT_MONTHS}ヶ月分
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 住所カードリスト編集 UI (請求先 / 納品先で共有)
// ─────────────────────────────────────────────

function AddressListEditor({
  entries,
  emptyLabel,
  placeholderPrefix,
  onUpdate,
  onRemove,
  onSetDefault,
}: {
  entries: AddressForm[];
  emptyLabel: string;
  placeholderPrefix: string;
  onUpdate: (id: string, patch: Partial<AddressForm>) => void;
  onRemove: (id: string) => void;
  onSetDefault: (id: string) => void;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-xs text-[var(--text-muted)] italic">{emptyLabel}</p>
    );
  }
  return (
    <div className="space-y-3">
      {entries.map((a, idx) => (
        <div
          key={a.id}
          className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <MapPin className="size-3.5 text-[var(--text-muted)] shrink-0" />
              <Input
                value={a.label}
                onChange={(e) => onUpdate(a.id, { label: e.target.value })}
                placeholder={`${placeholderPrefix} ${idx + 1}（例: 本社、営業所A）`}
                className="text-xs"
              />
            </div>
            <button
              type="button"
              onClick={() => onSetDefault(a.id)}
              className={`px-2 py-1 text-[10px] rounded inline-flex items-center gap-1 transition-colors ${
                a.isDefault
                  ? 'bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]'
                  : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
              title={a.isDefault ? `既定の${placeholderPrefix}` : 'この住所を既定にする'}
            >
              <Star className={`size-3 ${a.isDefault ? 'fill-current' : ''}`} />
              {a.isDefault ? '既定' : '既定にする'}
            </button>
            <button
              type="button"
              onClick={() => onRemove(a.id)}
              className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent-red)] hover:bg-[var(--bg-hover)]"
              title={`この${placeholderPrefix}を削除`}
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="宛名">
              <Input
                value={a.recipientName}
                onChange={(e) =>
                  onUpdate(a.id, { recipientName: e.target.value })
                }
                placeholder="例: 山田 太郎"
              />
            </Field>
            <Field label="電話番号">
              <Input
                value={a.phone}
                onChange={(e) => onUpdate(a.id, { phone: e.target.value })}
                placeholder="例: 03-1234-5678"
              />
            </Field>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-3">
            <Field label="郵便番号">
              <Input
                value={a.postalCode}
                onChange={(e) => onUpdate(a.id, { postalCode: e.target.value })}
                placeholder="000-0000"
              />
            </Field>
            <Field label="都道府県">
              <Input
                value={a.prefecture}
                onChange={(e) => onUpdate(a.id, { prefecture: e.target.value })}
              />
            </Field>
          </div>
          <Field label="市区町村・番地">
            <Input
              value={a.address}
              onChange={(e) => onUpdate(a.id, { address: e.target.value })}
            />
          </Field>
          <Field label="ビル名・階数">
            <Input
              value={a.buildingName}
              onChange={(e) => onUpdate(a.id, { buildingName: e.target.value })}
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// 内部ヘルパ
// ─────────────────────────────────────────────

function initForm(c: MailContact) {
  // 旧データ互換: billingAddresses 未設定なら postalCode 等から 1 件合成
  const billing = getBillingAddresses(c);
  return {
    name: c.name ?? '',
    lastName: c.lastName ?? '',
    firstName: c.firstName ?? '',
    lastNameKana: c.lastNameKana ?? '',
    firstNameKana: c.firstNameKana ?? '',
    phone: c.phone ?? '',
    kind: (c.kind ?? 'unknown') as ContactKind,
    companyName: c.companyName ?? '',
    department: c.department ?? '',
    jobTitle: c.jobTitle ?? '',
    birthdate: c.birthdate ?? '',
    salesMemo: c.salesMemo ?? '',
    isTestContact: c.isTestContact === true,
    billingAddresses: billing.map(toAddressForm),
    deliveryAddresses: (c.deliveryAddresses ?? []).map(toAddressForm),
    // 英表記登録情報 (海外メーカー報告用)
    nameEn: c.nameEn ?? '',
    phoneIntl: c.phoneIntl ?? '',
    enCountry: c.englishAddress?.country ?? '',
    enAddr: {
      postalCode: c.englishAddress?.postalCode ?? '',
      stateRegion: c.englishAddress?.stateRegion ?? '',
      city: c.englishAddress?.city ?? '',
      line1: c.englishAddress?.line1 ?? '',
      line2: c.englishAddress?.line2 ?? '',
    } as Record<EnglishAddressFieldKey, string>,
  };
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
        {description && (
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-[var(--text-muted)] mb-1">{label}</span>
      {children}
    </label>
  );
}
